import { Stack } from '@mui/material';
import { KeyboardEvent, ReactNode } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import { useFieldError } from '../../contexts/FieldError.context';
import { resolveFieldEmptyLabel, resolveFieldValueColor } from '../../fieldValue.utils';
import Typography from '../../ui/Typography/Typography';
import { inlineEditErrorOutline } from '../inlineEdit.utils';

interface InlineEditDisplayProps {
  children: ReactNode;
  isEmpty: boolean;
  isDisabled: boolean;
  isMuted?: boolean;
  isMultiline?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  placeholder?: LangEnum;
  testId?: string;
  onStartEditing: () => void;
}

const InlineEditDisplay = ({
  children,
  isEmpty,
  isDisabled,
  isMuted = false,
  isMultiline = false,
  hasError = false,
  errorMessage,
  placeholder = LangEnum.GLOBAL_ADD,
  testId,
  onStartEditing,
}: InlineEditDisplayProps) => {
  const { translate } = useTranslation();
  const hasFieldError = useFieldError();

  const hasErrorMessage = Boolean(errorMessage);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onStartEditing();
    }
  };

  const value = (
    <Typography
      variant="body2"
      role={isDisabled ? undefined : 'button'}
      tabIndex={isDisabled ? undefined : 0}
      color={resolveFieldValueColor({
        hasError: hasError || hasFieldError || hasErrorMessage,
        isEmpty,
        isMuted,
        isEditable: !isDisabled,
      })}
      testId={testId}
      onClick={isDisabled ? undefined : onStartEditing}
      onKeyDown={isDisabled ? undefined : handleKeyDown}
      sx={(theme) => ({
        padding: theme.spacing(0.5, 0.75),
        borderRadius: `${theme.shape.borderRadiusMd}px`,
        cursor: isDisabled ? 'default' : 'text',
        whiteSpace: isMultiline ? 'pre-wrap' : 'normal',
        wordBreak: 'break-word',
        transition: theme.transitions.create('background-color'),
        ...inlineEditErrorOutline(theme, hasErrorMessage),
        ...(isDisabled ? {} : { '&:hover': { backgroundColor: 'action.hover' } }),
      })}
    >
      {isEmpty
        ? translate(resolveFieldEmptyLabel({ isEditable: !isDisabled, placeholder }))
        : children}
    </Typography>
  );

  if (!hasErrorMessage) {
    return value;
  }

  return (
    <Stack sx={{ gap: 0.25 }}>
      {value}
      <Typography variant="micro1" color="error.main" sx={{ paddingX: 0.75 }}>
        {errorMessage}
      </Typography>
    </Stack>
  );
};

export default InlineEditDisplay;
